/**
 * useVisitProjectTimes — per-project sub-totals for the store visit in progress
 * (#3452). Reads the task-session records and folds them with
 * `projectTimesForVisit`, refreshing whenever the visit's task state changes
 * (start / pause / resume / finish).
 */

import { useEffect, useState } from 'react';
import { getAllTaskSessions } from '../utils/visitDB';
import { projectTimesForVisit } from '../utils/visitProjectTime';
import type { ProjectTime } from '../utils/visitProjectTime';

export function useVisitProjectTimes(visitStart: Date | null, refreshKey: unknown) {
  const [times, setTimes] = useState<Map<number, ProjectTime>>(new Map());

  useEffect(() => {
    let cancelled = false;
    if (!visitStart) {
      setTimes(new Map());
      return;
    }
    (async () => {
      try {
        const sessions = await getAllTaskSessions();
        if (cancelled) return;
        setTimes(projectTimesForVisit(sessions, visitStart));
      } catch (err) {
        // A sub-total is decoration; never let it break the banner.
        console.warn('[useVisitProjectTimes] failed to read task sessions:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // visitStart is a Date object; key on its instant so a re-render with an
    // equal date doesn't re-read IDB.
  }, [visitStart?.getTime(), refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return times;
}
